'use client'

import { ext } from '@/utils/linkTarget'
import { useEffect, useState } from 'react'
import Container from '@/components/templates/container'

const WHATSAPP_URL = 'https://wa.me/6281337582672'
const LIMIT = 6

// PADEL = forest green, TENNIS = umber
const SPORT_COLOR = { PADEL: '#2f3a2b', TENNIS: '#4d402c' }
const sportColor = (sport) => SPORT_COLOR[sport] ?? '#857d66'

const titleCase = (str = '') =>
  str.trim().replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase())

const isAmericana = (e) => /americana|mexicana|mexicano/i.test(e.name || '')

function baliDate(offsetDays = 0) {
  return new Date(Date.now() + (8 + offsetDays * 24) * 60 * 60 * 1000).toISOString().slice(0, 10)
}

const spots = (e) => {
  if (e.isFull) return 'Full'
  const left = e.spotsLeft ?? (e.capacity ? e.capacity - e.participantCount : null)
  if (left == null) return 'Join'
  return left === 1 ? '1 spot left' : `${left} spots left`
}

function toRow(e, title) {
  return {
    id: e.id,
    url: e.url,
    sport: e.sport,
    title,
    when: `${e.startDate} · ${e.startTime}`,
    date: e.startDate,
    time: e.startTime,
    status: spots(e),
    full: !!e.isFull,
  }
}

const pill = (active) =>
  `min-h-[44px] px-5 py-2.5 border font-chivoMono text-[12px] uppercase tracking-widest transition-colors duration-200 ${
    active ? 'bg-forest border-forest text-background' : 'border-khaki/40 text-umber hover:border-forest'
  }`

const tabPill = (active) =>
  `min-h-[44px] px-3 py-2.5 border font-chivoMono text-[11px] uppercase tracking-wider whitespace-nowrap transition-colors duration-200 ${
    active ? 'bg-forest border-forest text-background' : 'border-khaki/40 text-umber hover:border-forest'
  }`

// sport: 'PADEL' | 'TENNIS' | undefined (filters everything to one sport)
// showOpenMatches: false on sport pages that don't list open matches
// heading: fixed title (e.g. 'On the courts'); defaults to 'Today' / 'This week'
// variant: 'list' (homepage) or 'cards' (session cards in a grid)
export default function TodayAtMahima({ sport, showOpenMatches = true, heading, variant = 'list' }) {
  const [data, setData] = useState(null)
  const [range, setRange] = useState('week')
  const [cat, setCat] = useState(null)
  const [more, setMore] = useState(false)

  useEffect(() => {
    fetch('/api/playtomic/today')
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ openMatches: [], tournaments: [], coaching: [] }))
  }, [])

  if (!data) return null

  let { openMatches = [], tournaments = [], coaching = [] } = data
  if (sport) {
    openMatches = openMatches.filter((m) => m.sport === sport)
    tournaments = tournaments.filter((t) => t.sport === sport)
    coaching = coaching.filter((s) => s.sport === sport)
  }

  const today = baliDate()
  const weekEnd = baliDate(7)
  const inRange = (e) => (range === 'today' ? e.date === today : e.date <= weekEnd)

  const lists = {
    americanas: tournaments.filter(inRange).filter(isAmericana).map((e) => toRow(e, titleCase(e.name))),
    tournaments: tournaments.filter(inRange).filter((e) => !isAmericana(e)).map((e) => toRow(e, titleCase(e.name))),
    ...(showOpenMatches && {
      matches: openMatches.filter(inRange).map((e) => toRow(e, `Open ${e.sport.toLowerCase()} match`)),
    }),
    coaching: coaching.filter(inRange).map((e) => toRow(e, (e.name || '').trim())),
  }
  const labels = { americanas: 'Americanas', tournaments: 'Tournaments', matches: 'Open matches', coaching: 'Coaching' }
  const nouns = { americanas: 'americanas', tournaments: 'tournaments', matches: 'open matches', coaching: 'group coaching sessions' }

  const hasAny = (showOpenMatches && openMatches.length) || tournaments.length || coaching.length
  if (!hasAny) return null

  const keys = Object.keys(lists)
  const active = cat && lists[cat] ? cat : keys.find((k) => lists[k].length) || keys[0]
  const all = lists[active]
  const rows = more ? all : all.slice(0, LIMIT)

  if (variant === 'cards') {
    return (
      <CardsLayout
        {...{ heading, sport, range, setRange, keys, labels, lists, active, setCat, setMore, all, more, nouns }}
      />
    )
  }

  return (
    <Container outerClassName="mt-[100px] lg:mt-[140px]">
      <section aria-labelledby="otc-title" className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-12 lg:gap-20">
        {/* Intro */}
        <div className="flex flex-col gap-7 lg:gap-8">
          <span className="font-chivoMono text-[11px] uppercase tracking-widest text-khaki">Live from Playtomic</span>
          <h2 id="otc-title" className="font-ttDrugsTrial text-olive text-[30px] md:text-[40px] leading-none">
            {heading || (range === 'today' ? 'Today' : 'This Week')}
          </h2>
          <p className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-umber max-w-[460px]">
            Join an open match, sign up for an americana or enter a tournament. Spots update live from Playtomic; if
            you don’t use Playtomic, message us on WhatsApp and we’ll add you.
          </p>
          <div role="tablist" aria-label="Time range" className="flex gap-2">
            {[['today', 'Today'], ['week', 'This week']].map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={range === id}
                onClick={() => { setRange(id); setMore(false) }}
                className={pill(range === id)}
              >
                {label}
              </button>
            ))}
          </div>
          {!sport && (
            <div className="flex gap-6 font-chivoMono text-[13px] text-umber">
              {['PADEL', 'TENNIS'].map((s) => (
                <span key={s} className="flex items-center gap-2">
                  <span aria-hidden="true" className="w-2 h-2 rounded-full" style={{ background: sportColor(s) }} />
                  {titleCase(s)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* List */}
        <div>
          {/* One line: tabs share the width, scroll sideways on small screens */}
          <div role="tablist" aria-label="Activity" className="flex gap-2 mb-7 overflow-x-auto scrollbar-hidden">
            {keys.map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={active === k}
                onClick={() => { setCat(k); setMore(false) }}
                className={`${tabPill(active === k)} flex-1 shrink-0`}
              >
                {labels[k]} · {lists[k].length}
              </button>
            ))}
          </div>

          <div className="border-t border-olive">
            {rows.map((r) => (
              <a
                key={r.id}
                href={r.url}
                {...ext(r.url)}
                className="flex items-baseline justify-between gap-5 py-5 border-b border-dune hover:bg-dune/40 transition-colors duration-200"
              >
                <span className="flex items-baseline gap-5 min-w-0">
                  <span className="shrink-0 w-[96px] md:w-[140px] font-chivoMono text-[12px] md:text-[13px] text-khaki">{r.when}</span>
                  <span className="flex items-center gap-3 min-w-0">
                    <span role="img" aria-label={titleCase(r.sport)} className="shrink-0 w-2 h-2 rounded-full" style={{ background: sportColor(r.sport) }} />
                    <span className="font-ttDrugsTrial text-olive text-[18px] md:text-[20px] leading-snug">{r.title}</span>
                  </span>
                </span>
                <span
                  className="shrink-0 font-chivoMono text-[12px] md:text-[13px] whitespace-nowrap"
                  style={{ color: r.full ? '#857d66' : sportColor(r.sport) }}
                >
                  {r.status} <span aria-hidden="true">→</span>
                </span>
              </a>
            ))}
            {all.length === 0 && (
              <p className="py-5 border-b border-dune font-chivoMono text-bodyMobile md:text-bodyDesktop text-khaki">
                No {nouns[active]} scheduled {range === 'today' ? 'today' : 'this week'}
              </p>
            )}
          </div>

          {all.length > LIMIT && (
            <button
              type="button"
              onClick={() => setMore((m) => !m)}
              className="min-h-[44px] mt-3 font-chivoMono text-[13px] text-olive underline underline-offset-4"
            >
              {more ? 'Show fewer' : `Show all ${all.length}`}
            </button>
          )}

          <div className="flex flex-wrap items-center gap-6 mt-8">
            <a
              href="https://playtomic.com/clubs/mahima"
              target="_blank"
              rel="noopener noreferrer"
              className="px-7 py-4 bg-forest text-background font-chivoMono text-[13px] uppercase tracking-widest hover:bg-umber transition-colors duration-300"
            >
              Book on Playtomic
            </a>
            <a
              href={WHATSAPP_URL}
              {...ext(WHATSAPP_URL)}
              className="pb-1 border-b border-olive font-chivoMono text-olive text-[13px] uppercase tracking-widest hover:text-umber"
            >
              Join via WhatsApp
            </a>
          </div>
        </div>
      </section>
    </Container>
  )
}

// ── Cards layout: header row, full-width tabs, a grid of session cards ────

const CARD_LIMIT = 8

function CardsLayout({ heading, sport, range, setRange, keys, labels, lists, active, setCat, setMore, all, more, nouns }) {
  const rows = more ? all : all.slice(0, CARD_LIMIT)
  return (
    <Container outerClassName="mt-[100px] lg:mt-[140px]">
      <section aria-labelledby="otc-cards-title">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div className="flex flex-col gap-5 lg:gap-6 max-w-[560px]">
            <span className="font-chivoMono text-[11px] uppercase tracking-widest text-khaki">Live from Playtomic</span>
            <h2 id="otc-cards-title" className="font-ttDrugsTrial text-olive text-[30px] md:text-[40px] leading-none">
              {heading || (range === 'today' ? 'Today' : 'This Week')}
            </h2>
            <p className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-umber">
              Americanas, tournaments and group classes at Mahima. Spots update live from Playtomic; if you don’t use
              Playtomic, message us on WhatsApp and we’ll add you.
            </p>
          </div>
          <div role="tablist" aria-label="Time range" className="flex gap-2 shrink-0">
            {[['today', 'Today'], ['week', 'This week']].map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={range === id}
                onClick={() => { setRange(id); setMore(false) }} className={pill(range === id)}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div role="tablist" aria-label="Activity" className="flex gap-2 mb-8 lg:mb-10 overflow-x-auto scrollbar-hidden border-b border-dune pb-6">
          {keys.map((k) => (
            <button key={k} type="button" role="tab" aria-selected={active === k}
              onClick={() => { setCat(k); setMore(false) }} className={`${tabPill(active === k)} shrink-0`}>
              {labels[k]} · {lists[k].length}
            </button>
          ))}
        </div>

        {all.length === 0 ? (
          <p className="py-6 font-chivoMono text-bodyMobile md:text-bodyDesktop text-khaki">
            No {nouns[active]} scheduled {range === 'today' ? 'today' : 'this week'}.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
            {rows.map((r) => (
              <a key={r.id} href={r.url} {...ext(r.url)}
                className={`group flex flex-col gap-2 p-4 lg:p-5 border border-dune hover:border-khaki transition-colors duration-200 ${r.full ? 'opacity-60' : ''}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-chivoMono text-[11px] uppercase tracking-widest text-khaki">{r.date}</span>
                  <span className="font-ttDrugsTrial text-olive text-[22px] leading-none">{r.time}</span>
                </div>
                <span className="font-ttDrugsTrial text-olive text-[17px] leading-snug">{r.title}</span>
                <div className="mt-auto pt-3 flex items-center justify-between gap-3 font-chivoMono text-[11px] uppercase tracking-widest">
                  <span className="flex items-center gap-2" style={{ color: r.full ? '#857d66' : sportColor(r.sport) }}>
                    {!sport && <span aria-hidden="true" className="w-2 h-2 rounded-full" style={{ background: sportColor(r.sport) }} />}
                    {r.status}
                  </span>
                  <span className="text-olive transition-transform duration-200 group-hover:translate-x-1">
                    {r.full ? 'Waitlist' : 'Book'} <span aria-hidden="true">→</span>
                  </span>
                </div>
              </a>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-6 mt-8 lg:mt-10">
          <div className="flex flex-wrap items-center gap-6">
            <a href="https://playtomic.com/clubs/mahima" target="_blank" rel="noopener noreferrer"
              className="px-7 py-4 bg-forest text-background font-chivoMono text-[13px] uppercase tracking-widest hover:bg-umber transition-colors duration-300">
              Book on Playtomic
            </a>
            <a href={WHATSAPP_URL} {...ext(WHATSAPP_URL)}
              className="pb-1 border-b border-olive font-chivoMono text-olive text-[13px] uppercase tracking-widest hover:text-umber">
              Join via WhatsApp
            </a>
          </div>
          {all.length > CARD_LIMIT && (
            <button type="button" onClick={() => setMore((m) => !m)}
              className="min-h-[44px] font-chivoMono text-[13px] text-olive underline underline-offset-4">
              {more ? 'Show fewer' : `Show all ${all.length}`}
            </button>
          )}
        </div>
      </section>
    </Container>
  )
}
